import { Col, DatePicker, Form, FormInstance, Input, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback } from 'react';
import { Columns } from '../components/admin/Table';
import TableWithDrawer from '../components/admin/TableWithDrawer';
import PageHeader from '../components/base/PageHeader';
import {
  Album,
  GetAlbumsQuery,
  GetAlbumsQueryVariables,
  useCreateAlbumMutation,
  useDeleteAlbumMutation,
  useGetAlbumsQuery,
  useUpdateAlbumMutation,
} from '../graphql/schema';
import formItemProps from '../helper/formItemProps';

const columns: Columns<Album> = {
  name: 'Name',
};

interface FormFields {
  name: string;
  dateStart?: Dayjs;
  dateEnd?: Dayjs;
  password?: string;
  slug?: string;
}

const ManageAlbums = () => {
  const [create] = useCreateAlbumMutation();
  const [update] = useUpdateAlbumMutation();
  const [remove] = useDeleteAlbumMutation();

  const handleCreate = useCallback(
    async (values: FormFields) => {
      await create({
        variables: {
          dto: {
            name: values.name,
            dateStart: values.dateStart,
            dateEnd: values.dateEnd,
            password: values.password,
          },
        },
      });
    },
    [create],
  );

  const handleUpdate = useCallback(
    async (id: string, values: FormFields) => {
      await update({
        variables: {
          id,
          dto: {
            name: values.name,
            dateStart: values.dateStart,
            dateEnd: values.dateEnd,
            password: values.password,
            slug: values.slug,
          },
        },
      });
    },
    [update],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await remove({
        variables: {
          id,
        },
      });
    },
    [remove],
  );

  const formRenderer =
    (isUpdateForm = false) =>
    (form: FormInstance) => {
      return (
        <Form layout="vertical" form={form}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item {...formItemProps('name', 'Name', true)}>
                <Input placeholder="Name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item {...formItemProps('password', 'Passwort')}>
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item {...formItemProps('dateStart', 'Startdatum')}>
                <DatePicker allowClear />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item {...formItemProps('dateEnd', 'Enddatum')}>
                <DatePicker allowClear />
              </Form.Item>
            </Col>
            {isUpdateForm && (
              <Col span={24}>
                <Form.Item {...formItemProps('slug', 'Slug', true)}>
                  <Input />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      );
    };

  return (
    <>
      <PageHeader title="Fotoalben" />

      <TableWithDrawer<
        FormFields,
        Album,
        GetAlbumsQuery,
        GetAlbumsQueryVariables
      >
        query={useGetAlbumsQuery}
        columns={columns}
        createFormRenderer={formRenderer()}
        editFormRenderer={formRenderer(true)}
        singleItemTitle="Album"
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        prepareFormValues={(item) => ({
          name: item.name ?? '',
          dateStart: item.dateStart ? dayjs(item.dateStart) : undefined,
          dateEnd: item.dateEnd ? dayjs(item.dateEnd) : undefined,
          password: item.password ?? undefined,
          slug: item.slug ?? undefined,
        })}
      />
    </>
  );
};

export default ManageAlbums;
